'use client';

import { TeamsApi } from '@up2stay/up2promo-api-client';
import {
  Button,
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@up2stay/up2promo-ui';
import { useState } from 'react';
import { toast } from 'sonner';

import { triggerTapHaptic } from '@/core/lib/dnd-haptics';

interface DeleteTeamDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  teamId: string;
  teamName: string;
  onSuccess: () => void;
  dict: {
    title: string;
    description: string;
    confirm: string;
    deleting: string;
    cancel: string;
    success: string;
    error: string;
  };
}

export function DeleteTeamDialog({
  open,
  onOpenChange,
  workspaceId,
  teamId,
  teamName,
  onSuccess,
  dict,
}: DeleteTeamDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await TeamsApi.remove({ path: { workspaceId, teamId } });
      toast.success(dict.success);
      onOpenChange(false);
      onSuccess();
    } catch (err) {
      console.error('Failed to delete team:', err);
      toast.error(dict.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={(next) => {
        void triggerTapHaptic();
        onOpenChange(next);
      }}
    >
      <ResponsiveDialogContent className="sm:max-w-[480px]">
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>{dict.title}</ResponsiveDialogTitle>
          <ResponsiveDialogDescription>
            {dict.description.replace('{name}', teamName)}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        <ResponsiveDialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            {dict.cancel}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? dict.deleting : dict.confirm}
          </Button>
        </ResponsiveDialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
